import { isSecurityIssuedInCurrency } from '@dealstack/frontend/shared/cap';
import { Table } from '@tanstack/react-table';
import { numericFormatter } from 'react-number-format';
import {
	CAP_TABLE_NUMBER_FORMAT,
	CAP_TABLE_VALUES_HELD_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { numberToCost } from '@dealstack/frontend/shared/other/react/number.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import {
	CurrencyType,
	HolderValues,
	Receiver,
	SecurityIssuedIn,
} from '@dealstack/shared/lib/generated';
import { numberToCompact, numberToUiPercent } from '@dealstack/shared/lib/format/number.ts';

export const getCellValue = ({
	holderValues,
	valuesHeldType,
	showSummarized,
	securityIssuedIn,
	currency,
	showPercentages,
}: {
	holderValues: HolderValues;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	showSummarized: boolean;
	currency?: CurrencyType;
	securityIssuedIn?: SecurityIssuedIn;
	showPercentages: boolean;
}): string | number | undefined => {
	return formatCapValue({
		holderValues,
		valuesHeldType,
		showSummarized,
		isLoan: isSecurityIssuedInCurrency(securityIssuedIn),
		currency,
		showPercentages,
	});
};

export const formatCapValue = ({
	holderValues,
	valuesHeldType,
	showSummarized,
	isLoan,
	currency,
	showPercentages,
}: {
	holderValues: HolderValues;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	showSummarized: boolean;
	isLoan?: boolean;
	currency?: CurrencyType;
	showPercentages: boolean;
}): string | number | undefined => {
	if (showPercentages) {
		if (isLoan) {
			if (valuesHeldType === CAP_TABLE_VALUES_HELD_TYPE.VOTES) {
				return;
			}
			return numberToUiPercent(holderValues.totalCostPercent);
		}

		if (valuesHeldType === CAP_TABLE_VALUES_HELD_TYPE.VOTES) {
			if (!holderValues.votesPercent) {
				return;
			}
			return numberToUiPercent(holderValues.votesPercent);
		}

		if (valuesHeldType === CAP_TABLE_VALUES_HELD_TYPE.SECURITIES) {
			if (!holderValues.amountPercent) {
				return;
			}
			return numberToUiPercent(holderValues.amountPercent);
		}
	} else {
		if (isLoan) {
			if (valuesHeldType === CAP_TABLE_VALUES_HELD_TYPE.VOTES) {
				return;
			}
			const value = showSummarized
				? numberToCompact(holderValues.totalCost)
				: numberToCost(holderValues.totalCost);

			if (currency) {
				return `${value} ${currency}`;
			}

			return value;
		}

		if (valuesHeldType === CAP_TABLE_VALUES_HELD_TYPE.VOTES && holderValues.votes) {
			return formatFlatNumber(holderValues.votes ?? 0, showSummarized);
		}

		if (valuesHeldType === CAP_TABLE_VALUES_HELD_TYPE.SECURITIES) {
			if (!holderValues.amount) {
				return;
			}

			return formatCapNumber(
				holderValues.amount,
				showSummarized ? CAP_TABLE_NUMBER_FORMAT.SHORTENED : CAP_TABLE_NUMBER_FORMAT.FULL
			);
		}
	}
};

export const checkIsIpGroupExpanded = <TableRow>(
	receiver: Receiver,
	table: Table<TableRow>
): boolean => {
	const isExpanded = table
		.getVisibleFlatColumns()
		.some((column) => column.id.includes(`|${receiver.ipId}`));
	return isExpanded;
};

export const formatFlatNumber = (value: number, showSummarized: boolean) => {
	if (!value) {
		return LONG_DASH;
	}

	if (showSummarized) {
		return numberToCompact(value);
	}

	return formatNumber({ value });
};

const formatScaledNumber = (value: number, divisor: number, suffix: string) => {
	const scaled = value / divisor;
	if (Math.round(Math.abs(scaled) * 100) === 0) {
		return `<0.01${suffix}`;
	}
	return `${formatNumber({
		value: scaled,
		minPrecision: 0,
		maxPrecision: 2,
		negativeStyle: 'minus',
	})}${suffix}`;
};

/** Scaled formats keep up to 2 decimals; a non-zero value too small for that reads as <0.01. */
export const formatCapNumber = (value: number, numberFormat: CAP_TABLE_NUMBER_FORMAT): string => {
	if (value === 0) {
		return '0';
	}

	switch (numberFormat) {
		case CAP_TABLE_NUMBER_FORMAT.FULL:
			return numericFormatter(value.toString(), { thousandSeparator: ',' });
		case CAP_TABLE_NUMBER_FORMAT.SHORTENED:
			return numberToCompact(value);
		case CAP_TABLE_NUMBER_FORMAT.THOUSANDS:
			return formatScaledNumber(value, 1_000, 'K');
		case CAP_TABLE_NUMBER_FORMAT.MILLIONS:
			return formatScaledNumber(value, 1_000_000, 'M');
		case CAP_TABLE_NUMBER_FORMAT.BILLIONS:
			return formatScaledNumber(value, 1_000_000_000, 'B');
	}
};

/**
 * Extracts the entity ID from a receiver ID for navigation purposes.
 *
 * The backend creates composite IDs (entityId|mipId) to distinguish between
 * direct holdings and investment programme holdings for the same entity.
 * This is essential for cap table accuracy and legal compliance.
 *
 * We handle this in the frontend rather than changing the backend because
 * the composite ID structure serves legitimate business purposes and changing
 * it would require extensive backend refactoring.
 *
 * @param receiverId - The receiver ID, which may be composite (e.g., "entityId|mipId")
 * @returns The entity ID (first part if composite, or the full ID if simple)
 */
export const extractEntityIdFromReceiver = (receiverId: string): string => {
	return receiverId.includes('|') ? receiverId.split('|')[0] : receiverId;
};
